import { DetailedHTMLProps, ForwardedRef, HTMLAttributes, forwardRef, useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { AvatarOptions } from '../helpers/avatar.types';
import { getFontSize, getInitials } from '../helpers/avatar.utils';

interface AvatarProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, AvatarOptions {
    ref?: ForwardedRef<HTMLDivElement>;
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
    const {
        src,
        alt,
        backgroundColor = 'rgb(var(--primary))',
        size = 32,
        fontSize,
        initialsOnly = true,
        className,
        style,
        ...rest
    } = props;

    const [isError, setIsError] = useState<boolean>(false);

    const altFontSize = useMemo(() => (fontSize ? fontSize : getFontSize(size)), [size, fontSize]);

    const showImage = useMemo(() => src && !isError, [isError, src]);

    const renderAlt = useCallback(() => {
        if (initialsOnly) return getInitials(alt);

        return alt;
    }, [alt, initialsOnly]);

    return (
        <div
            {...rest}
            ref={ref}
            className={twMerge(
                'relative flex items-center justify-center flex-shrink-0 select-none rounded-full overflow-hidden text-white text-center font-medium',
                className
            )}
            style={{
                height: size,
                width: size,
                fontSize: altFontSize,
                backgroundColor: !showImage ? backgroundColor : undefined,
                ...style,
            }}
        >
            {showImage ? (
                <img
                    className=" w-full h-full object-cover object-center rounded-full text-transparent bg-grayscale-100"
                    src={src}
                    alt=""
                    onError={() => setIsError(true)}
                />
            ) : (
                renderAlt()
            )}
        </div>
    );
});

export default Avatar;
